import {
  BreakpointsEnum,
  SVG,
  isBreakpointDown,
  isBreakpointUp,
  useBreakpoint,
  useIsAndroid,
  useIsDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import firstWave from 'assets/OfferCardsWaveTypes/wave_1.svg';
import secondWave from 'assets/OfferCardsWaveTypes/wave_2.svg';
import thirdWave from 'assets/OfferCardsWaveTypes/wave_3.svg';
import fourthWave from 'assets/OfferCardsWaveTypes/wave_4.svg';
import {Box, Pressable, Text, View} from 'native-base';
import React, {RefObject, useEffect, useMemo, useRef} from 'react';
import {Platform, UIManager} from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome5Pro';

import useHover from 'hooks/useHover';
import {OfferCardSize, useOfferCardTheme} from 'hooks/useOfferCardTheme';
import {Offer, OfferSource, OfferType, RewardState} from 'models/Offer';
import {Properties} from 'store/properties/properties.interface';
import {getOfferTypeIcon} from 'utils/getOfferTypeIcon';

import HeartFavorite from './HeartFavorite';
import OfferExpirationDate from './OfferExpirationDate';
import OfferState from './OfferState';
import {useTheme} from 'styled-components/native';
import {useRoute} from '@react-navigation/native';
import {Routes} from 'models/Navigation';
import {OFFERS_GROUPING} from '../OfferGroup';

export type OfferCardProps = {
  offer: Offer;
  properties: Properties;
  onFavoritePress: () => void;
  onNextPress: () => void;
  testID?: string;
  isOfferPrefLoading: boolean;
  isListView: boolean;
  /**
   * Additional string of property values to display on the card
   * used when there is no matching offer.propertyList value to an AEM property.
   */
  additionalPropertyCodes?: string[];
  clearFocusedCardId: () => void;
  focusedCardId: string | undefined;
  selectedOfferId: string | undefined;
  cardIndex?: number;
  isInCurrentGrouping?: boolean;
};

export const renderPropertyList = (
  properties: string[],
  maxChars: number,
  propertyObjectArray: Properties
): string => {
  const numProperties = properties?.length;
  if (!numProperties) {
    return '';
  }

  if (numProperties > 1) {
    if (propertyObjectArray.every(p => p.marketCode === 'lvm')) {
      return 'Las Vegas Resorts';
    }
    return 'Multiple Locations';
  }

  const first = (properties[0] || '').trim();
  const truncated =
    first.length <= maxChars
      ? first
      : `${first.substring(0, maxChars).trim()}...`;

  return truncated;
};

const getWaveSvg = (i: number) => {
  if (i === 0 || i % 4 === 0) {
    return firstWave;
  }
  if (i === 3 || (i - 3) % 4 === 0) {
    return fourthWave;
  }
  if (i % 2 === 0) {
    return thirdWave;
  }
  return secondWave;
};

const OfferCard: React.FC<OfferCardProps> = props => {
  const {
    offer,
    properties,
    onFavoritePress,
    onNextPress,
    testID,
    additionalPropertyCodes,
    isOfferPrefLoading,
    clearFocusedCardId,
    focusedCardId,
    selectedOfferId,
    cardIndex = 0,
    isListView = false,
    isInCurrentGrouping = false,
  } = props;
  const {title, end, pref, status, start} = offer;
  const {fontSizes, colors} = useTheme() as any;
  const {paleSky, tuna, caesarRewards, white} = colors;
  const {hovering, handleHoverIn, handleHoverOut} = useHover();
  const route = useRoute();
  const routeName = route?.name || Routes.Main.Offers;
  const {
    height,
    width,
    titleFontSize,
    titleHeight,
    titleLineHeight,
    mainPadding,
    propertiesCharLimit,
    propertiesFontSize,
    statusFontSize,
    expiryMargin,
    cardSize,
  } = useOfferCardTheme(routeName);

  const breakpoint = useBreakpoint();
  const isNano = isBreakpointDown(breakpoint, BreakpointsEnum.NANO);
  const isXsmall = isBreakpointDown(breakpoint, BreakpointsEnum.XS);
  const isDesktop = useIsDesktop();
  const isTwoRowLayout = !isNano && isXsmall;
  const isLargeDesktop = isBreakpointUp(breakpoint, BreakpointsEnum.XXL);
  const listView =
    cardSize === OfferCardSize.NANO || (isListView && !isDesktop);

  const offerRewardError =
    offer.source === OfferSource.ocrm &&
    offer.ocrm.rewards.some(reward => reward.state === RewardState.ERROR);

  const showStatus =
    !!status &&
    status !== 'Active' &&
    status !== 'Offered' &&
    status !== 'Bookable' &&
    !offerRewardError;

  const propertyNames = useMemo(() => {
    const p = properties.map(property => property.propertyName);

    if (additionalPropertyCodes) {
      p.push(...additionalPropertyCodes);
    }

    return p;
  }, [additionalPropertyCodes, properties]);

  const offerType = (): OfferType => {
    switch (offer.source) {
      case OfferSource.pdb:
        return offer.refinedType;
      case OfferSource.ocrm:
        if (offer.ocrm.rewards.length === 1) {
          return offer.ocrm.rewards[0].refinedType;
        }
        return OfferType.Other;
      default:
        return OfferType.Other;
    }
  };

  const cardRef = useRef();

  useEffect(() => {
    if (
      focusedCardId === offer.id &&
      !selectedOfferId &&
      isInCurrentGrouping &&
      Platform.OS === 'web'
    ) {
      UIManager.focus(cardRef.current);
      clearFocusedCardId();
    }
  }, [
    clearFocusedCardId,
    focusedCardId,
    offer.id,
    selectedOfferId,
    isInCurrentGrouping,
  ]);

  const waveSvg = isLargeDesktop ? getWaveSvg(cardIndex) : '';

  return (
    <Pressable
      ref={cardRef as RefObject<any>}
      accessibilityRole="button"
      accessibilityLabel="Open Offer Details"
      testID={testID}
      onPress={onNextPress}
      shadow={Platform.OS === 'web' ? (hovering ? 3 : 0) : 1}
      justifyContent="space-between"
      width={isTwoRowLayout ? '100%' : width}
      minWidth={width}
      height={height}
      zIndex={1}
      backgroundColor={white}
      borderRadius={listView ? 8 : 4}
      padding={mainPadding}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}>
      {isLargeDesktop && (
        <SVG source={waveSvg} style={{position: 'absolute', top: 0, left: 0}} />
      )}
      <Box flex={1}>
        {listView ? (
          <View flexDirection="row" width="100%" justifyContent="space-between">
            <View height="24px" width="24px">
              <SVG
                alt={`${offerType()} Offer`}
                source={getOfferTypeIcon({
                  offerType: offerType(),
                  isSelected: true,
                })}
              />
            </View>
            <View alignSelf="flex-start">
              <HeartFavorite
                cardSize={cardSize}
                pref={pref}
                onFavoritePress={onFavoritePress}
                isOfferLoading={isOfferPrefLoading}
              />
            </View>
          </View>
        ) : (
          <>
            <View alignSelf="flex-end">
              <HeartFavorite
                cardSize={cardSize}
                pref={pref}
                onFavoritePress={onFavoritePress}
                isOfferLoading={isOfferPrefLoading}
              />
            </View>
            <View
              width="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="row">
              <View
                borderBottomColor={paleSky}
                borderBottomWidth={1}
                flex={1}
                opacity={0.2}
              />
              <View marginX="5px" height="24px" width="24px">
                {
                  <SVG
                    alt={`${offerType()} Offer`}
                    source={getOfferTypeIcon({
                      offerType: offerType(),
                      isSelected: true,
                    })}
                  />
                }
              </View>
              <View
                borderBottomColor={paleSky}
                borderBottomWidth={1}
                flex={1}
                opacity={0.2}
              />
            </View>
          </>
        )}

        <View
          height={titleHeight}
          mt={listView ? '6px' : '16px'}
          mb={listView ? null : '16px'}>
          <Text
            testID="offer-title"
            textAlign={listView ? 'left' : 'center'}
            fontSize={titleFontSize}
            fontFamily="heading"
            lineHeight={titleLineHeight}
            fontWeight={800}
            overflow="hidden"
            textTransform="uppercase"
            numberOfLines={3}
            isTruncated={true}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            style={{
              ...(Platform.OS === 'web'
                ? {
                    marginBottom: '-4px',
                    whiteSpace: 'pre-line',
                  }
                : {height: 90}),
            }}
            color={tuna}>
            {title}
          </Text>
        </View>
        <Box flex={1} justifyContent="flex-start" mt={listView ? '8px' : null}>
          <View height={listView ? undefined : '28px'}>
            <Text
              testID="offer-property-list"
              color={paleSky}
              fontSize={propertiesFontSize}
              numberOfLines={isNano ? 1 : 2}
              lineHeight="14px"
              textAlign={listView ? 'left' : 'center'}
              fontWeight={700}>
              {propertyNames.length
                ? renderPropertyList(
                    propertyNames,
                    propertiesCharLimit,
                    properties
                  )
                : ' '}
            </Text>
          </View>
          <View height={`${expiryMargin}px`} />
          <View
            justifyContent={listView ? 'space-between' : 'center'}
            flexDirection={listView ? 'row' : 'column'}>
            <OfferExpirationDate
              startDate={start}
              endDate={end}
              size={propertiesFontSize}
              lineHeight={14}
            />
            {showStatus && (
              <OfferState
                status={status}
                size={statusFontSize}
                isListView={listView}
              />
            )}
          </View>
        </Box>
      </Box>
      {isDesktop && (
        <Box
          height={
            showStatus
              ? `${90 - expiryMargin - 35}px`
              : `${90 - expiryMargin}px`
          }
          justifyContent="flex-end"
          alignItems="flex-end">
          <View
            alignItems="center"
            justifyContent="center"
            height={36}
            width={36}
            borderRadius={18}
            borderWidth={1}
            backgroundColor={hovering ? caesarRewards : white}
            borderColor={hovering ? caesarRewards : 'rgba(112,119,132,0.3)'}>
            <Icon
              name="long-arrow-right"
              solid={true}
              color={hovering ? white : paleSky}
              size={fontSizes[4]}
            />
          </View>
        </Box>
      )}
    </Pressable>
  );
};

export default OfferCard;
